/**
 * Overzicht van alle geabonneerde apparaten (sessie 4, 09-09-2026).
 *
 * De meting van 09-09 vond precies één abonnement in de database
 * terwijl JP een MacBook, een Mac Studio en een iPhone gebruikt: elke
 * `web_push.melding_verstuurd`-logregel meldde daarom `totaal=1`. De
 * wortel was niet het verzenden maar de zichtbaarheid — het blok
 * hierboven toont alleen de stand van *dit* apparaat, en een
 * abonnement dat de pushdienst afwees werd stil verwijderd. Wie op zijn
 * Mac keek zag "aan" en kon niet weten dat de iPhone er niet meer bij
 * zat.
 *
 * Dit blok laat daarom alle apparaten van het account zien, met sinds
 * wanneer het abonnement loopt, wanneer er voor het laatst echt een
 * melding aankwam, en of de pushdienst hem inmiddels afwijst. Per
 * apparaat één testknop, zodat een uitslag altijd bij één toestel
 * hoort.
 *
 * Er komt hier nooit een push-endpoint of sleutel over de lijn: het
 * endpoint is een capability-URL, de server stuurt alleen de stand.
 */

import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, MonitorSmartphone, Trash2 } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { pushApi, vertaalApiFout } from "@/lib/api";
import {
  abonneerBrowser,
  huidigBrowserAbonnement,
  naarAbonnementInvoer,
} from "@/lib/push";
import type { PushApparaat } from "@/types/api";

/** Pill in de huisstijl van het portaal (zelfde vorm als elders). */
function Pil({
  toon,
  children,
}: {
  toon: "neutraal" | "alarm";
  children: React.ReactNode;
}) {
  const kleur =
    toon === "alarm"
      ? "border-red-200 bg-red-50 text-red-700"
      : "border-border bg-muted text-muted-foreground";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11.5px] font-medium ${kleur}`}
    >
      {children}
    </span>
  );
}

/** Datum + tijd, kort en zonder seconden ("8 sep 16:01"). */
function kortMoment(iso: string): string {
  return new Date(iso).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function PushApparaten() {
  const { t } = useTranslation();
  const [bezigId, setBezigId] = useState<string | null>(null);
  const [opruimen, setOpruimen] = useState(false);

  const query = useQuery({
    queryKey: ["push", "apparaten"],
    queryFn: async () => {
      // Het eigen endpoint meesturen zodat de server "dit apparaat"
      // kan aanwijzen; de browser kent het, de lijst geeft het niet
      // terug.
      const eigen = await huidigBrowserAbonnement().catch(() => null);
      return pushApi.apparaten(eigen?.endpoint ?? null);
    },
    // Het blok is geen live-dashboard: verversen gebeurt na een test of
    // een opruimactie, want dán is de stand veranderd.
    staleTime: 60 * 1000,
  });
  const apparaten = query.isError ? [] : (query.data ?? null);
  const laad = useCallback(async () => {
    await query.refetch();
  }, [query]);

  const test = useCallback(
    async (apparaat: PushApparaat) => {
      setBezigId(apparaat.id);
      try {
        const uitkomst = await pushApi.testmelding(apparaat.id);
        if (uitkomst.geslaagd > 0) {
          toast.success(t("profiel.push_apparaat_test_geslaagd"));
        } else if (uitkomst.verlopen > 0) {
          toast.error(t("profiel.push_apparaat_test_verlopen"));
        } else {
          toast.error(t("profiel.push_apparaat_test_mislukt"));
        }
        await laad(); // de stand van dit apparaat is net bijgewerkt
      } catch (fout) {
        toast.error(vertaalApiFout(fout));
      } finally {
        setBezigId(null);
      }
    },
    [laad, t],
  );

  // Sessie 48 (F1): dit toestel opnieuw inschrijven. Oude inschrijving
  // eraf (bij ons én in de browser), een verse aanvragen en opslaan met de
  // voorkeuren die er al waren.
  const [inschrijven, setInschrijven] = useState(false);
  const schrijfOpnieuwIn = useCallback(
    async (apparaat: PushApparaat) => {
      setInschrijven(true);
      try {
        const oud = await huidigBrowserAbonnement().catch(() => null);
        if (oud) {
          await pushApi.afmelden(oud.endpoint).catch(() => undefined);
          await oud.unsubscribe().catch(() => undefined);
        }
        const config = await pushApi.config();
        if (!config.publieke_sleutel) throw new Error("push_niet_geconfigureerd");
        const nieuw = await abonneerBrowser(config.publieke_sleutel);
        await pushApi.abonneer(naarAbonnementInvoer(nieuw, apparaat.voorkeuren));
        toast.success("Dit toestel is opnieuw ingeschreven. Stuur een testmelding.");
        await laad();
      } catch (fout) {
        toast.error(vertaalApiFout(fout));
      } finally {
        setInschrijven(false);
      }
    },
    [laad],
  );

  const ruimOp = useCallback(async () => {
    setOpruimen(true);
    try {
      const uitkomst = await pushApi.opruimen();
      toast.success(
        t("profiel.push_apparaat_opgeruimd", { count: uitkomst.opgeruimd }),
      );
      await laad();
    } catch (fout) {
      toast.error(vertaalApiFout(fout));
    } finally {
      setOpruimen(false);
    }
  }, [laad, t]);

  if (apparaten === null) {
    return null; // eerste laadslag: geen leeg kader laten flitsen
  }

  const verlopenAantal = apparaten.filter((a) => a.verlopen_op).length;

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand-cyan-soft text-brand-cyan-dark grid place-items-center">
            <MonitorSmartphone className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-[15px] font-semibold">
              {t("profiel.push_apparaten_titel")}
            </h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              {t("profiel.push_apparaten_uitleg")}
            </p>
          </div>
        </div>

        {apparaten.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">
            {t("profiel.push_apparaten_geen")}
          </p>
        ) : (
          <ul className="divide-y" data-testid="push-apparaten">
            {apparaten.map((apparaat) => (
              <li
                key={apparaat.id}
                className="py-3 flex items-start justify-between gap-4 first:pt-0"
              >
                <div className="min-w-0">
                  <p className="text-[13.5px] font-medium flex items-center gap-2 flex-wrap">
                    {apparaat.apparaat ?? t("profiel.push_apparaat_naamloos")}
                    {apparaat.dit_apparaat && (
                      <Pil toon="neutraal">
                        {t("profiel.push_apparaat_dit")}
                      </Pil>
                    )}
                    {apparaat.verlopen_op && (
                      <Pil toon="alarm">
                        {t("profiel.push_apparaat_verlopen")}
                      </Pil>
                    )}
                  </p>
                  <p className="text-[12.5px] text-muted-foreground mt-0.5">
                    {t("profiel.push_apparaat_sinds", {
                      moment: kortMoment(apparaat.sinds),
                    })}
                  </p>
                  {apparaat.soort ? (
                    <p className="text-[12.5px] text-muted-foreground" data-push-soort>
                      {apparaat.soort}
                    </p>
                  ) : null}
                  <p className="text-[12.5px] text-muted-foreground">
                    {apparaat.laatste_geslaagd_op
                      ? t("profiel.push_apparaat_laatste", {
                          moment: kortMoment(apparaat.laatste_geslaagd_op),
                        })
                      : t("profiel.push_apparaat_nog_nooit")}
                  </p>
                  <p className="text-[12.5px] text-muted-foreground" data-push-getoond>
                    {apparaat.laatste_ontvangen_op
                      ? `Laatst getoond op het toestel: ${kortMoment(apparaat.laatste_ontvangen_op)}`
                      : "Nog geen melding teruggemeld door het toestel."}
                  </p>
                  {apparaat.niet_getoond ? (
                    <div
                      className="text-[12.5px] text-red-700 mt-1 space-y-1"
                      data-push-niet-getoond
                    >
                      <p>
                        De pushdienst nam de laatste melding aan
                        {apparaat.laatste_melding_op
                          ? ` (${kortMoment(apparaat.laatste_melding_op)})`
                          : ""}
                        , maar dit toestel meldde niet dat hij hem liet zien.
                        Aan onze kant is hij dus weg; het toestel hield hem tegen.
                      </p>
                      {(apparaat.apparaat ?? "").match(/iPhone|iPad/) ? (
                        <ol className="list-decimal pl-5 space-y-0.5 text-foreground">
                          <li>
                            Open de werkplek vanaf het icoon op je beginscherm, niet
                            in Safari. Alleen de beginscherm-app krijgt meldingen.
                          </li>
                          <li>
                            Staat er geen icoon: open klantenservice.voeronline-hub.nl
                            in Safari, tik Deel en Zet op beginscherm.
                          </li>
                          <li>
                            Instellingen, Meldingen, VO Klantenservice: meldingen aan,
                            en geen Focus-modus die ze tegenhoudt.
                          </li>
                          <li>
                            Open in de app Mijn profiel en tik hier Opnieuw
                            inschrijven, dan Test.
                          </li>
                        </ol>
                      ) : null}
                    </div>
                  ) : null}
                  {apparaat.verlopen_op ? (
                    <p className="text-[12.5px] text-red-700 mt-0.5">
                      {t("profiel.push_apparaat_verlopen_uitleg", {
                        moment: kortMoment(apparaat.verlopen_op),
                      })}
                    </p>
                  ) : (
                    apparaat.laatste_fout_op && (
                      <p className="text-[12.5px] text-red-700 mt-0.5">
                        {t("profiel.push_apparaat_fout", {
                          moment: kortMoment(apparaat.laatste_fout_op),
                          status: apparaat.laatste_fout_status ?? "-",
                        })}
                        {apparaat.fout_tekst ? ` ${apparaat.fout_tekst}` : null}
                      </p>
                    )
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0 items-end">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      bezigId === apparaat.id || Boolean(apparaat.verlopen_op)
                    }
                    onClick={() => test(apparaat)}
                  >
                    {bezigId === apparaat.id && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {t("profiel.push_apparaat_test")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-push-opnieuw
                    disabled={!apparaat.dit_apparaat || inschrijven}
                    title={
                      apparaat.dit_apparaat
                        ? undefined
                        : "Kan alleen op het toestel zelf: open Mijn profiel daar."
                    }
                    onClick={() => schrijfOpnieuwIn(apparaat)}
                  >
                    {inschrijven && apparaat.dit_apparaat && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    Opnieuw inschrijven
                  </Button>
                  {!apparaat.dit_apparaat ? (
                    <span className="text-[11px] text-muted-foreground max-w-[12rem] text-right">
                      Opnieuw inschrijven kan alleen op dat toestel zelf.
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}

        {verlopenAantal > 0 && (
          <div className="border-t pt-3 flex items-center justify-between gap-4">
            <p className="text-[12.5px] text-muted-foreground">
              {t("profiel.push_apparaat_opruimen_uitleg", {
                count: verlopenAantal,
              })}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              disabled={opruimen}
              onClick={ruimOp}
            >
              {opruimen ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {t("profiel.push_apparaat_opruimen")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
